import { FC, useRef, useState } from 'react';

import { regionFromDaumAddress } from '@shared/utils/giftAddressRegion';
import type { Address } from 'react-daum-postcode';
import DaumPostcode from 'react-daum-postcode';

import { RecipientModalState } from '../utils/delivery.types';

interface Props {
  modal: RecipientModalState;
  setModal: React.Dispatch<React.SetStateAction<RecipientModalState | null>>;
  onSave: () => Promise<void>;
}

const inputClassName =
  'mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500';

export const RecipientEditModal: FC<Props> = ({ modal, setModal, onSave }) => {
  // 주소는 직접 입력받지 않고 우편번호 검색 결과로만 채운다.
  // 주소 문자열만 고치고 우편번호가 옛것으로 남는 사고를 막기 위함 (둘은 항상 함께 바뀐다).
  const [postcodeOpen, setPostcodeOpen] = useState(false);
  const addressDetailRef = useRef<HTMLInputElement>(null);

  const handleAddressComplete = (data: Address) => {
    // 수령자 배송지 입력 화면과 같은 조합 규칙 (건물명이 있으면 괄호로 덧붙임)
    const fullAddress =
      data.address + (data.buildingName ? ` (${data.buildingName})` : '');
    setModal((prev) =>
      prev
        ? {
            ...prev,
            postalCode: data.zonecode,
            address: fullAddress,
            // 권역 배송비 매칭 근거 — 주소와 함께 원문 그대로 저장
            region: regionFromDaumAddress(data),
          }
        : null,
    );
    setPostcodeOpen(false);
    // 주소가 바뀌면 동/호수도 대개 함께 바뀐다. 바로 고칠 수 있게 커서를 옮긴다.
    setTimeout(() => addressDetailRef.current?.focus(), 0);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={() => setModal(null)} />
      <div className="relative w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
        <h3 className="text-lg font-bold text-gray-900">수령인 정보 수정</h3>
        <p className="mt-1 text-xs text-gray-500">
          발주 전 수령인 및 배송지 정보를 수정할 수 있습니다.
        </p>

        <div className="mt-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">수령인</label>
              <input
                type="text"
                value={modal.name}
                onChange={(e) =>
                  setModal((prev) => (prev ? { ...prev, name: e.target.value } : null))
                }
                className={inputClassName}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">연락처</label>
              <input
                type="text"
                value={modal.phoneNumber}
                onChange={(e) =>
                  setModal((prev) =>
                    prev ? { ...prev, phoneNumber: e.target.value } : null,
                  )
                }
                placeholder="01012345678"
                className={inputClassName}
              />
            </div>
          </div>

          {/* 주소 + 우편번호 — 검색 결과로 함께 갱신 (개별 수정 불가) */}
          <div>
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-gray-700">
                주소 <span className="text-xs font-normal text-gray-400">(우편번호)</span>
              </label>
              {modal.address && (
                <button
                  type="button"
                  onClick={() => setPostcodeOpen(true)}
                  className="text-xs font-semibold text-indigo-600 hover:text-indigo-700"
                >
                  주소 변경
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={() => setPostcodeOpen(true)}
              className={`mt-1 block w-full rounded-md border px-3 py-2 text-left text-sm ${
                modal.address
                  ? 'border-gray-300 text-gray-800 hover:bg-gray-50'
                  : 'border-dashed border-gray-300 text-gray-400 hover:bg-gray-50'
              }`}
            >
              {modal.address ? (
                <span>
                  {modal.postalCode && (
                    <span className="mr-1 text-gray-500">({modal.postalCode})</span>
                  )}
                  {modal.address}
                </span>
              ) : (
                <span>주소 검색</span>
              )}
            </button>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">상세주소</label>
            <input
              ref={addressDetailRef}
              type="text"
              value={modal.addressDetail}
              onChange={(e) =>
                setModal((prev) =>
                  prev ? { ...prev, addressDetail: e.target.value } : null,
                )
              }
              placeholder="동/호수 등 상세주소"
              className={inputClassName}
            />
            <p className="mt-1 text-xs text-gray-400">
              상세주소만 고칠 때는 주소 검색 없이 이 칸만 수정하면 됩니다.
            </p>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button
            onClick={() => setModal(null)}
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            취소
          </button>
          <button
            disabled={modal.isSaving}
            onClick={async () => {
              await onSave();
            }}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {modal.isSaving ? '저장 중...' : '저장'}
          </button>
        </div>
      </div>

      {/* 우편번호 검색 — 수정 모달 위에 겹쳐 띄운다 (배경 클릭은 검색창만 닫음) */}
      {postcodeOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div
            className="fixed inset-0 bg-black/50"
            onClick={() => setPostcodeOpen(false)}
          />
          <div className="relative w-full max-w-lg overflow-hidden rounded-xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b px-4 py-3">
              <h4 className="text-sm font-bold text-gray-900">주소 검색</h4>
              <button
                type="button"
                onClick={() => setPostcodeOpen(false)}
                className="rounded-md px-2 py-1 text-sm text-gray-500 hover:bg-gray-100"
              >
                닫기
              </button>
            </div>
            <DaumPostcode style={{ height: 480 }} onComplete={handleAddressComplete} />
          </div>
        </div>
      )}
    </div>
  );
};
